import React, { FC, ReactNode, useCallback } from 'react';
import { WithTranslation } from 'react-i18next';
import { ActionBarProps } from '../ActionBar';

import PrimaryButton from '../PrimaryButton';
import SecondaryButton from '../SecondaryButton';

import { StyledActionBar } from './ConfirmBar.styles';

export interface ConfirmBarProps extends ActionBarProps {
  children?: ReactNode;
  /** Callback for the cancel button. */
  onCancel?: () => void;
  /** Callback for the confirm button. */
  onConfirm?: () => void | Promise<void>;
  /** Custom text for the cancel buton. Defaults to "Cancel". */
  cancelButton?: ReactNode;
  /** Custom text for the confirm buton. Defaults to "Confirm". */
  confirmButton?: ReactNode;
  /** Disables the cancel button. */
  cancelDisabled?: boolean;
  /** Focus the confirm button. */
  confirmButtonFocus?: boolean;
  /** Disables the confirm button. */
  confirmDisabled?: boolean;
  /** Hide the cancel button. */
  cancelHidden?: boolean;
  /** Hide the confirm button. */
  confirmHidden?: boolean;
  /** Display the cancel button as busy. */
  cancelBusy?: boolean;
  /** Display the confirm buttona as busy. */
  confirmBusy?: boolean;
  /** Defines the alignment of the text, title and buttons. */
  align?: 'left' | 'center' |'right';
}

const StyledBarDefaultProps = {
  cancelHidden: false,
  confirmHidden: false,
  cancelBusy: false,
  confirmBusy: false,
  confirmButtonFocus: false,
};

const ConfirmBar: FC<ConfirmBarProps & WithTranslation> = ({
  t,
  onCancel,
  onConfirm,
  cancelHidden,
  confirmHidden,
  cancelButton,
  confirmButton,
  cancelDisabled,
  confirmDisabled,
  confirmButtonFocus,
  cancelBusy,
  confirmBusy,
  ...props
}) => {
  const handleSubmit = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    onConfirm();
  }, [onConfirm]);

  return (
    <StyledActionBar {...props}>
      {!cancelHidden && onCancel && (
        <SecondaryButton
          disabled={cancelDisabled}
          onClick={onCancel}
          busy={cancelBusy}
          data-testid="ConfirmBarCancelButton"
        >
          {cancelButton || t('cancelButton')}
        </SecondaryButton>
      )}
      {!confirmHidden && onConfirm && (
        <PrimaryButton
          autoFocus={confirmButtonFocus}
          disabled={confirmDisabled}
          busy={confirmBusy}
          data-testid="ConfirmBarConfirmButton"
          onClick={handleSubmit}
        >
          {confirmButton || t('confirmButton')}
        </PrimaryButton>
      )}
    </StyledActionBar>
  );
};

ConfirmBar.defaultProps = StyledBarDefaultProps;

export default ConfirmBar;
